import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, setAuthToken } from '../services/api';

interface User {
  id: string;
  email: string;
  accountId: string;
  account: {
    id: string;
    name: string;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    const savedToken = localStorage.getItem('token');
    console.log('Initial token from localStorage:', savedToken);
    return savedToken;
  });
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for authentication
  useEffect(() => {
    console.log('Setting up axios with token:', token ? 'Token exists' : 'No token');
    setAuthToken(token);
    if (token) {
      console.log('Authorization header set for token:', token.substring(0, 10) + '...');
    } else {
      console.log('Authorization header removed');
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      console.log('Checking auth with token:', token);
      if (token) {
        try {
          const response = await authApi.me();
          console.log('Auth check response:', response);
          setUser(response.user);
        } catch (error: any) {
          console.error('Auth check failed:', error);
          // Only clear token if it's actually invalid (401/403), not on network errors
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.log('Token is invalid, clearing...');
            localStorage.removeItem('token');
            setToken(null);
          } else {
            console.log('Network error, keeping token for retry');
          }
        }
      }
      setLoading(false);
    };

    // Add a small delay to ensure the axios interceptor is set up
    const timeoutId = setTimeout(checkAuth, 100);
    return () => clearTimeout(timeoutId);
  }, [token]);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      console.log('Login response:', response);
      const { token: newToken, user: userData } = response;

      console.log('Extracted token:', newToken);
      console.log('Extracted user:', userData);

      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);

        // Verify token was saved
        const savedToken = localStorage.getItem('token');
        console.log('Token saved to localStorage:', savedToken);
        console.log('Token matches:', savedToken === newToken);
      } else {
        console.error('No token received from login response');
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.error || 'Login failed');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      const response = await authApi.register(email, password);
      console.log('Register response:', response);
      const { token: newToken, user: userData } = response;

      console.log('Extracted token:', newToken);
      console.log('Extracted user:', userData);

      if (newToken) {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(userData);

        // Verify token was saved
        const savedToken = localStorage.getItem('token');
        console.log('Token saved to localStorage:', savedToken);
        console.log('Token matches:', savedToken === newToken);
      } else {
        console.error('No token received from register response');
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      console.error('Register error:', error);
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setAuthToken(null);
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
